import logisticsConfig from '@/lib/service/ecs/logisticsConfig.json';

export interface ServiceCentreAddress {
  name: string;
  address1: string;
  address2?: string;
  address3?: string;
  phone?: string;
  email?: string;
}

export type RepairDeliveryMethod = 'send' | 'boutique';
export type RepairReturnMethod = 'clientShipping' | 'boutiquePickup';
/** ECS modeOfTransport values — kept for upstream contract parity */
export type RepairShippingKitOption = 'BreitlingSending' | 'ClientSending';

export interface RepairLogisticsCapabilities {
  countryCode: string;
  csPlatformAddress: ServiceCentreAddress;
  allowDirectSendToCSPlatform: boolean;
  allowBoutiqueDropOff: boolean;
  hasBrandShippingKit: boolean;
  allowReceiveAtHome: boolean;
  allowRetrieveInBoutique: boolean;
}

const CONFIG_BY_COUNTRY = new Map<string, RepairLogisticsCapabilities>(
  (logisticsConfig as RepairLogisticsCapabilities[]).map((entry) => [
    entry.countryCode.toUpperCase(),
    entry,
  ])
);

const DEFAULT_CAPABILITIES: RepairLogisticsCapabilities = {
  countryCode: 'US',
  csPlatformAddress: {
    name: '',
    address1: '',
  },
  allowDirectSendToCSPlatform: true,
  allowBoutiqueDropOff: false,
  hasBrandShippingKit: false,
  allowReceiveAtHome: true,
  allowRetrieveInBoutique: false,
};

export function getRepairLogisticsCapabilities(countryCode: string): RepairLogisticsCapabilities {
  const normalized = countryCode.trim().toUpperCase();
  return CONFIG_BY_COUNTRY.get(normalized) ?? { ...DEFAULT_CAPABILITIES, countryCode: normalized };
}

export function resolveDefaultDeliveryMethod(
  capabilities: RepairLogisticsCapabilities,
  stored: RepairDeliveryMethod | null = null
): RepairDeliveryMethod {
  if (!capabilities.allowBoutiqueDropOff) {
    return 'send';
  }
  if (!capabilities.allowDirectSendToCSPlatform) {
    return 'boutique';
  }
  return stored ?? 'send';
}

export function resolveDefaultReturnMethod(
  capabilities: RepairLogisticsCapabilities,
  stored: RepairReturnMethod | null = null
): RepairReturnMethod {
  if (!capabilities.allowRetrieveInBoutique) {
    return 'clientShipping';
  }
  if (!capabilities.allowReceiveAtHome) {
    return 'boutiquePickup';
  }
  return stored ?? 'clientShipping';
}

export function resolveDefaultShippingKitOption(
  capabilities: RepairLogisticsCapabilities,
  stored: RepairShippingKitOption | null = null
): RepairShippingKitOption {
  if (!capabilities.hasBrandShippingKit) {
    return 'ClientSending';
  }
  return stored ?? 'BreitlingSending';
}

export function isServiceBoutiqueRequired(
  deliveryMethod: RepairDeliveryMethod,
  returnMethod: RepairReturnMethod
): boolean {
  return deliveryMethod === 'boutique' || returnMethod === 'boutiquePickup';
}
